import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Logo imports with correct, case-sensitive names
import intersect from '../assets/images/intersect.png';
import imdaLogo from '../assets/images/IMDA-Logo.png';
import banner from '../assets/images/microsoft-banner1.jpeg';
import tradeTrustLogo from '../assets/images/TT-LOGO.png';

const PartnerSection = () => {
    const [activeId, setActiveId] = useState('default');

    const partners = {
        default: {
            title: "Trusted by Leaders.",
            subtitle: "Recognized for Innovation.",
            isDefault: true
        },
        intersect: {
            title: "Intersect",
            description: "ZenithBlox is a member of Intersect, a member-based organization focused on advancing the Cardano ecosystem. Intersect acts as a collaborative hub for organizations to drive the development, governance, and sustainable growth of the Cardano blockchain. As a member, ZenithBlox has a voice in the ecosystem's future, with opportunities to vote on governance matters, access community grants, and collaborate with other key players to strengthen Cardano's position in blockchain technology."
        },
        microsoft: {
            title: "Microsoft for Startups",
            description: "ZenithBlox is a partner with Microsoft for Startups. This program is designed to help new companies grow by providing them with technology, expert guidance, and resources. Through this partnership, ZenithBlox gains access to Microsoft's powerful tools and platforms, including Azure credits which can be used for a range of AI and cloud services, including OpenAI models. This support allows ZenithBlox to build and scale its blockchain integration solutions more efficiently, future-proof its business, and accelerate development."
        },
        imda: {
            title: "IMDA",
            description: "ZenithBlox has a strategic partnership with the Infocomm Media Development Authority (IMDA) of Singapore to enhance secure, interoperable, and legally compliant digital trade. As part of this collaboration, ZenithBlox is officially listed as a TradeTrust-enabled Business Solution Provider. This alliance aims to accelerate innovation and reinforce Singapore's position as a leading digital hub by combining IMDA's work in digital transformation with ZenithBlox's no-code integration platform. For governments, this partnership provides a ready-to-deploy solution for digital document management, customs, and procurement."
        },
        tradetrust: {
            title: "TradeTrust",
            description: "ZenithBlox has fully integrated TradeTrust, a framework developed by Singapore's IMDA, into its Universal Adapters. This integration allows businesses to authenticate the origin and integrity of trade documents using digital signatures, transfer legal titles for documents like Bills of Lading via NFTs and smart contracts, and seamlessly connect their existing systems (like ERP and CRM tools) with blockchain networks. The goal is to reduce manual processes, decrease fraud risks, and cut operational costs in cross-border trade."
        }
    };

    const logoData = [
        { id: 'intersect', src: intersect, alt: 'Intersect Logo' },
        { id: 'microsoft', src: banner, alt: 'Microsoft for Startups Logo' },
        { id: 'imda', src: imdaLogo, alt: 'IMDA Logo' },
        { id: 'tradetrust', src: tradeTrustLogo, alt: 'TradeTrust Logo' }
    ];

    const descriptionVariants = {
        initial: { opacity: 0, y: 15 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -15 },
    };

    return (
        <div className="w-full flex items-center justify-center p-4 bg-black">
            <div className="bg-[#131313] rounded-3xl w-full max-w-7xl border border-gray-800 p-6 md:p-12 lg:py-16 lg:px-16">
                <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">

                    {/* Left side: Dynamic Description Area */}
                    <div className="lg:w-5/12 text-center lg:text-left relative w-full min-h-[200px] lg:min-h-0">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={activeId}
                                variants={descriptionVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={{ duration: 0.3, ease: 'easeInOut' }}
                            >
                                {partners[activeId]?.isDefault || !partners[activeId] ? (
                                    <>
                                        <span className="inline-block border border-[#817AF7] text-[#817AF7] rounded-full px-4 py-1 text-sm font-medium mb-6 w-fit mx-auto lg:mx-0">
                                            Partnership
                                        </span>
                                        <h2 className="font-bold text-white text-3xl md:text-4xl">Trusted by Leaders.</h2>
                                        <h2 className="font-bold text-white text-3xl md:text-4xl">Recognized for Innovation.</h2>
                                    </>
                                ) : (
                                    <>
                                        <h3 className="font-bold text-white mb-3 text-2xl md:text-3xl">{partners[activeId].title}</h3>
                                        <p className="leading-relaxed text-gray-400 text-sm md:text-base">{partners[activeId].description}</p>
                                    </>
                                )}
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    {/* Right side: Logo grid */}
                    <div
                        className="lg:w-7/12 w-full grid grid-cols-2 gap-x-8 gap-y-10"
                        onMouseLeave={() => setActiveId('default')}
                    >
                        {logoData.map((logo) => (
                            <div
                                key={logo.id}
                                className="flex items-center justify-center cursor-pointer h-32 p-4"
                                onMouseEnter={() => setActiveId(logo.id)}
                            >
                                <img
                                    src={logo.src}
                                    alt={logo.alt}
                                    className="max-h-full max-w-full object-contain transition-opacity duration-300 hover:opacity-75"
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PartnerSection;